/** Column definitions for the notification feature-flags grid.
 *
 * All columns are `filterable: false` — filtering is driven entirely by the
 * QuerySearch widget. Sortable columns mirror the server's ordering whitelist
 * (feature/level/enabled/tenant/created_ts/updated_ts/id). `enabled` is an
 * inline mutating Switch (the grid IS the editor — no drawer); `scope` is
 * polymorphic on the row's level (tag rows summarize their chip condition);
 * `row_actions` carries the tag-row edit pencil — the one mutable-scope
 * exception.
 *
 * Every value that names something else is a `ValueActions` trigger
 * (`featureFlagLinks`): hover for the sibling rows and the grids that scope
 * reaches. The affordances the cells already own keep their click — the scope
 * link still navigates, the Switch still toggles.
 */
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { Box, Chip, IconButton, Link, Switch, Tooltip, Typography } from '@mui/material';
import type { GridColDef } from '@mui/x-data-grid-pro';
import { Fragment } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import type { FeatureFlagListItem } from '@/api/notificationFeatures';
import { copyableColumn } from '@/components/DataGrid/copyableColumn';
import { WithTenantMark } from '@/components/TenantMark/TenantMark';
import { utcTitle, ValueActions } from '@/components/ValueActions/ValueActions';
import { formatRelativeTime } from '@/utils/format';

import {
  enabledActions,
  featureActions,
  flagTagChipActions,
  flagTimestampActions,
  levelActions,
  scopeActions,
  updatedByActions,
} from './featureFlagLinks';
import { summarizeTagRow } from './tagFlagUtils';

const mono = { fontFamily: 'monospace', fontSize: 12 } as const;

function scopeLink(to: string, label: string) {
  return (
    <Link
      component={RouterLink}
      to={to}
      onClick={(e) => e.stopPropagation()}
      sx={{ color: '#0a1a3f' }}
    >
      {label}
    </Link>
  );
}

/** Compact tag-condition summary: ≤2 chips interleaved with the match-mode
 *  word, a `+N` overflow, and a tooltip carrying the full list + sources. */
function TagScopeCell({ row }: { row: FeatureFlagListItem }) {
  const s = summarizeTagRow(row);
  // `summarizeTagRow` maps the row's chips 1:1, so label i belongs to entry i —
  // the raw entry is what carries the tag and category IDS the labels drop.
  const entries = row.tags ?? [];
  const shown = s.labels.slice(0, 2);
  const extra = s.labels.length - shown.length;
  const modeWord = s.mode === 'and' ? 'AND' : 'OR';
  return (
    <Tooltip
      title={
        <Box>
          <Typography variant="caption" component="div">
            Requires {s.mode === 'and' ? 'ALL' : 'ANY'} of:
          </Typography>
          {s.labels.map((l, i) => (
            <Typography key={i} variant="caption" component="div">
              • {l}
            </Typography>
          ))}
          <Typography variant="caption" component="div" sx={{ mt: 0.5 }}>
            Sources: {s.sources.join(', ') || '—'}
          </Typography>
        </Box>
      }
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
        {shown.map((l, i) => {
          const entry = entries[i];
          const chip = <Chip size="small" variant="outlined" label={l} sx={{ maxWidth: 170 }} />;
          return (
            <Fragment key={i}>
              {i > 0 && (
                <Typography variant="caption" color="text.secondary">
                  {modeWord}
                </Typography>
              )}
              {entry ? (
                <ValueActions
                  chip
                  actions={flagTagChipActions(entry, { tenantId: row.tenant?.id ?? null })}
                >
                  {chip}
                </ValueActions>
              ) : (
                chip
              )}
            </Fragment>
          );
        })}
        {extra > 0 && (
          <Typography variant="caption" color="text.secondary">
            {modeWord} +{extra}
          </Typography>
        )}
      </Box>
    </Tooltip>
  );
}

/** The scope cell's own link keeps its click; the popover only adds the
 *  sibling rows and the grids that scope reaches. */
function withScopeActions(row: FeatureFlagListItem, content: JSX.Element) {
  const actions = scopeActions(row);
  return actions.length > 0 ? <ValueActions actions={actions}>{content}</ValueActions> : content;
}

function ScopeCell({ row }: { row: FeatureFlagListItem }) {
  switch (row.level) {
    case 'system':
      return (
        <Typography variant="body2" sx={{ fontStyle: 'italic' }} color="text.secondary">
          Everywhere
        </Typography>
      );
    case 'tenant':
      return row.tenant ? (
        <WithTenantMark environment={row.tenant.environment}>
          {withScopeActions(
            row,
            scopeLink(
              `/tour-operators/${row.tenant.id}`,
              row.tenant.code || row.tenant.name || `#${row.tenant.id}`,
            ),
          )}
        </WithTenantMark>
      ) : (
        <>—</>
      );
    case 'ota':
      return row.ota ? (
        withScopeActions(row, scopeLink(`/otas/${row.ota.id}`, row.ota.name ?? `#${row.ota.id}`))
      ) : (
        <>—</>
      );
    case 'tour':
      return row.tour ? (
        withScopeActions(
          row,
          scopeLink(`/tours/${row.tour.id}`, `${row.tour.name ?? 'Tour'} #${row.tour.id}`),
        )
      ) : (
        <>—</>
      );
    case 'notification':
      return row.notification ? (
        withScopeActions(
          row,
          scopeLink(
            `/notifications/${row.notification.id}`,
            row.notification.name ?? `#${row.notification.id}`,
          ),
        )
      ) : (
        <>—</>
      );
    case 'tag':
      return <TagScopeCell row={row} />;
  }
}

export function buildColumns(opts: {
  featureNames: Map<string, string>;
  canManage: boolean;
  togglePending: boolean;
  onToggle: (id: number, enabled: boolean) => void;
  /** Open the tag-row edit dialog (the pencil only renders on tag rows). */
  onEditTagRow: (row: FeatureFlagListItem) => void;
}): GridColDef<FeatureFlagListItem>[] {
  const { featureNames, canManage, togglePending, onToggle, onEditTagRow } = opts;
  return [
    copyableColumn({ field: 'id', headerName: 'ID', width: 80 }),
    {
      field: 'feature',
      headerName: 'Feature',
      width: 240,
      filterable: false,
      renderCell: (p) => {
        const name = featureNames.get(p.row.feature);
        return (
          <ValueActions actions={featureActions(p.row.feature)}>
            {name ?? p.row.feature}{' '}
            {name && (
              <Typography component="span" variant="caption" color="text.secondary" sx={mono}>
                {p.row.feature}
              </Typography>
            )}
          </ValueActions>
        );
      },
    },
    {
      field: 'level',
      headerName: 'Level',
      width: 130,
      filterable: false,
      renderCell: (p) => (
        <ValueActions chip actions={levelActions(p.row.level)}>
          <Chip size="small" variant="outlined" label={p.row.level} />
        </ValueActions>
      ),
    },
    {
      field: 'scope',
      headerName: 'Scope',
      flex: 1,
      minWidth: 200,
      sortable: false,
      filterable: false,
      renderCell: (p) => <ScopeCell row={p.row} />,
    },
    {
      // An `exclude` row reads identically to a `require` one in every other
      // column while doing the opposite thing, so the polarity has to be
      // visible in the grid — not only inside the edit dialog.
      field: 'polarity',
      headerName: 'Polarity',
      width: 150,
      sortable: false,
      filterable: false,
      renderCell: (p) => {
        if (p.row.level !== 'tag') return null;
        const excluding = p.row.polarity === 'exclude';
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Chip
              size="small"
              variant={excluding ? 'filled' : 'outlined'}
              color={excluding ? 'warning' : 'default'}
              label={excluding ? 'exclude' : 'require'}
            />
            {p.row.channel ? <Chip size="small" variant="outlined" label={p.row.channel} /> : null}
            {p.row.compiled ? (
              <Tooltip title="Written by the reseller-policy compiler. Editing it will be reported as diverged on the next recompile.">
                <Chip size="small" variant="outlined" color="info" label="compiled" />
              </Tooltip>
            ) : null}
          </Box>
        );
      },
    },
    {
      field: 'enabled',
      headerName: 'Enabled',
      width: 100,
      type: 'boolean',
      filterable: false,
      // The Switch owns the click (it stops propagation); hover adds the
      // "every enabled / disabled row" search.
      renderCell: (p) => (
        <ValueActions chip actions={enabledActions(p.row.enabled)}>
          <Switch
            size="small"
            checked={p.row.enabled}
            disabled={!canManage || togglePending}
            onClick={(e) => e.stopPropagation()}
            onChange={(e) => onToggle(p.row.id, e.target.checked)}
            inputProps={{ 'aria-label': `${p.row.feature} enabled` }}
          />
        </ValueActions>
      ),
    },
    {
      field: 'comment',
      headerName: 'Comment',
      flex: 1,
      minWidth: 160,
      sortable: false,
      filterable: false,
      valueGetter: (_, row) => row.comment ?? '',
    },
    {
      field: 'updated_by',
      headerName: 'Updated by',
      width: 170,
      sortable: false,
      filterable: false,
      valueGetter: (_, row) =>
        row.updated_by
          ? `${row.updated_by.first_name ?? ''} ${row.updated_by.last_name ?? ''}`.trim() ||
            (row.updated_by.email ?? `#${row.updated_by.id}`)
          : '',
      renderCell: (p) =>
        p.row.updated_by ? (
          <ValueActions actions={updatedByActions(p.row.updated_by)}>{p.value}</ValueActions>
        ) : (
          ''
        ),
    },
    {
      field: 'updated_ip',
      headerName: 'Updater IP',
      width: 130,
      sortable: false,
      filterable: false,
      valueGetter: (_, row) => row.updated_ip ?? '',
    },
    {
      field: 'created_ts',
      headerName: 'Created',
      width: 150,
      filterable: false,
      valueGetter: (_, row) => (row.created_ts ? formatRelativeTime(row.created_ts) : ''),
      renderCell: (p) =>
        p.row.created_ts ? (
          <ValueActions
            title={utcTitle(p.row.created_ts)}
            actions={flagTimestampActions('created', p.row.created_ts)}
          >
            {p.value}
          </ValueActions>
        ) : (
          ''
        ),
    },
    {
      field: 'updated_ts',
      headerName: 'Updated',
      width: 150,
      filterable: false,
      valueGetter: (_, row) => (row.updated_ts ? formatRelativeTime(row.updated_ts) : ''),
      renderCell: (p) =>
        p.row.updated_ts ? (
          <ValueActions
            title={utcTitle(p.row.updated_ts)}
            actions={flagTimestampActions('updated', p.row.updated_ts)}
          >
            {p.value}
          </ValueActions>
        ) : (
          ''
        ),
    },
    {
      field: 'row_actions',
      headerName: '',
      width: 56,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (p) =>
        p.row.level === 'tag' && canManage ? (
          <IconButton
            size="small"
            aria-label="Edit tag condition"
            onClick={(e) => {
              e.stopPropagation();
              onEditTagRow(p.row);
            }}
          >
            <EditOutlinedIcon fontSize="small" />
          </IconButton>
        ) : null,
    },
  ];
}
